import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { STALE } from '@/shared/api/stale';

import { customerKeys, type CustomerListParams } from './keys';

export const customersQuery = (p: CustomerListParams) =>
  queryOptions({
    queryKey: customerKeys.list(p),
    queryFn: ({ signal }) => unwrap(api.GET('/customers', { params: { query: { ...p } }, signal })),
    staleTime: STALE.list,
    placeholderData: keepPreviousData,
  });

export const customerQuery = (id: string) =>
  queryOptions({
    queryKey: customerKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.GET('/customers/{customerId}', { params: { path: { customerId: id } }, signal })),
    staleTime: STALE.record,
  });

export const customerDealsQuery = (id: string) =>
  queryOptions({
    queryKey: customerKeys.deals(id),
    queryFn: ({ signal }) => unwrap(api.GET('/deals', { params: { query: { customerId: id, size: 100, sort: '-updatedAt' } }, signal })),
    staleTime: STALE.list,
  });

/** Customer picker search (server-side). */
export function useCustomerSearch(q: string) {
  return useQuery({
    queryKey: customerKeys.picker(q),
    queryFn: ({ signal }) => unwrap(api.GET('/customers', { params: { query: { q, size: 20, sort: 'name' } }, signal })),
    staleTime: STALE.list,
    placeholderData: keepPreviousData,
  });
}

export const customerActivityQuery = (id: string) =>
  queryOptions({
    queryKey: customerKeys.activity(id),
    queryFn: ({ signal }) => unwrap(api.GET('/customers/{customerId}/activity', { params: { path: { customerId: id } }, signal })),
    staleTime: STALE.record,
  });

export const customerDuplicatesQuery = (id: string) =>
  queryOptions({
    queryKey: customerKeys.duplicates(id),
    queryFn: ({ signal }) => unwrap(api.GET('/customers/{customerId}/duplicates', { params: { path: { customerId: id } }, signal })),
    staleTime: 0,
  });

/** Active employers from the employer master, for the employment picker (server-side search). */
export function useEmployerOptions(q: string) {
  return useQuery({
    queryKey: customerKeys.employerOptions(q),
    queryFn: ({ signal }) => unwrap(api.GET('/employers', { params: { query: { q, size: 20, sort: 'name', status: 'active' } }, signal })),
    staleTime: STALE.reference,
    placeholderData: keepPreviousData,
  });
}

/** Every version of a customer document (newest first). */
export const customerDocumentVersionsQuery = (customerId: string, documentId: string) =>
  queryOptions({
    queryKey: customerKeys.documentVersions(customerId, documentId),
    queryFn: ({ signal }) => unwrap(api.GET('/customers/{customerId}/documents/{documentId}/versions', { params: { path: { customerId, documentId } }, signal })),
    staleTime: STALE.record,
  });

type Disposition = 'attachment' | 'inline';

/** A 5-minute link to the current file of a customer document (clean files only). */
export const fetchCustomerDocumentLink = (customerId: string, documentId: string, disposition: Disposition) =>
  unwrap(api.GET('/customers/{customerId}/documents/{documentId}/download', { params: { path: { customerId, documentId }, query: { disposition } } }));

/** A 5-minute link to one version of a customer document. */
export const fetchCustomerDocumentVersionLink = (customerId: string, documentId: string, versionId: string, disposition: Disposition) =>
  unwrap(api.GET('/customers/{customerId}/documents/{documentId}/versions/{versionId}/download', { params: { path: { customerId, documentId, versionId }, query: { disposition } } }));

/** Identity correction requests on the customer (B1), newest first. */
export const customerCorrectionsQuery = (id: string) =>
  queryOptions({
    queryKey: customerKeys.corrections(id),
    queryFn: async ({ signal }) => (await unwrap(api.GET('/customers/{customerId}/correction-requests', { params: { path: { customerId: id } }, signal }))).items,
    staleTime: STALE.record,
  });
